import { useCallback, useEffect, useState } from 'react';
import useRecipeV2Api from './useRecipeV2Api';
import { RecipeVersionDetail, RecipeVersionHistory } from '../types';

type SelectedVersions = [number | null, number | null];

const useRecipeVersions = (recipeId: number) => {
  const api = useRecipeV2Api();
  const [versions, setVersions] = useState<RecipeVersionHistory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  const [selectedVersions, setSelectedVersions] = useState<SelectedVersions>([null, null]);
  const [detailByVersion, setDetailByVersion] = useState<Record<number, RecipeVersionDetail>>({});
  const [isCompareLoading, setIsCompareLoading] = useState(false);
  const [compareError, setCompareError] = useState<string | null>(null);

  const refreshVersions = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      setVersions(await api.getVersionHistory(recipeId));
    } catch (cause) {
      setLoadError(cause instanceof Error ? cause.message : 'Failed to load recipe version history.');
      setVersions([]);
    } finally {
      setIsLoading(false);
    }
  }, [api, recipeId]);

  useEffect(() => {
    void refreshVersions();
  }, [refreshVersions]);

  const loadVersionDetail = useCallback(async (versionNumber: number): Promise<RecipeVersionDetail> => {
    if (detailByVersion[versionNumber]) return detailByVersion[versionNumber];

    const detail = await api.getVersionDetail(recipeId, versionNumber);
    setDetailByVersion((current) => ({ ...current, [versionNumber]: detail }));
    return detail;
  }, [api, detailByVersion, recipeId]);

  const openComparison = useCallback(async (versionA: number, versionB: number) => {
    setSelectedVersions([versionA, versionB]);
    setCompareDialogOpen(true);
    setCompareError(null);
    setIsCompareLoading(true);
    try {
      await Promise.all([loadVersionDetail(versionA), loadVersionDetail(versionB)]);
    } catch (cause) {
      setCompareError(cause instanceof Error ? cause.message : 'Failed to load recipe version details.');
    } finally {
      setIsCompareLoading(false);
    }
  }, [loadVersionDetail]);

  return {
    versions, isLoading, loadError, refreshVersions, compareDialogOpen, setCompareDialogOpen,
    selectedVersions, detailByVersion, isCompareLoading, compareError, openComparison,
  };
};

export default useRecipeVersions;
